import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  Chip as MuiChip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import Divider from "@mui/material/Divider";
import { useState, useRef } from "react";
import {
  createSearchParams,
  Link as RouterLink,
  useNavigate,
} from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import Chip from "src/components/common/Chip";
import { Button } from "src/components/common/FormComponents";
import { CHIP_STATUS, PROJECT_STATUS } from "src/constants/common";
import { path } from "src/constants/path";
import defaultStyles from "../styles";
import { useAppSelector } from "src/redux/store";
import { reviewDpr } from "src/utils/api/dpr";
import {
  uploadMou,
  updateMouDocument,
  submitMou,
  getMouByEpid,
  reviewMou,
} from "src/utils/api/mou";
import { fileUpload, getFileByKey } from "src/utils/api/fileupload";

export const RenderChip = (params) => {
  return (
    <Chip
      label={PROJECT_STATUS[params.row.chipTitle]}
      status={CHIP_STATUS[params.row.status]}
      description={params.row.remarks}
      showTooltip={PROJECT_STATUS[params.row.chipTitle] === "Rejected"}
    />
  );
};

export const RenderButton = (params) => {
  return (
    <>
      <Box
        component={RouterLink}
        to={`${"/activity/details"}?${createSearchParams({
          projectId: params.id,
          projectType: params.row.data.projectType,
        })}`}
        sx={{ ...defaultStyles.renderButtonText2 }}
      >{`${params?.row?.registrationNo}`}</Box>
    </>
  );
};

export const RenderDemandDraft = (params) => {
  const navigate = useNavigate();
  return (
    <>
      {params?.row?.status === "PAYMENT_PENDING" ? (
        <Box
          component={RouterLink}
          to={`${path.demandNote}?${createSearchParams({
            id: params.id,
          })}`}
          sx={{ ...defaultStyles.renderButtonText2 }}
        >{`${"Make Payment"}`}</Box>
      ) : (
        <Box sx={{ color: "red" }}>----</Box>
      )}
    </>
  );
};

export const RenderView = (params) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.root.user);
  return (
    <>
      <Box
        component={RouterLink}
        to={`${"/landownership-details"}?${createSearchParams({
          id: params.row.id,
        })}`}
        sx={{ ...defaultStyles.renderButtonText3 }}
      >
        {`${params?.row?.id}`}
      </Box>
    </>
  );
};

export const RenderUploadDPRButton = (params) => {
  const user = useAppSelector((state) => state.root.user);
  const dprStatus = params.row.rawDprStatus;
  const isEntity = user.data.userType === "ORGANIZATION";

  let label = isEntity ? "Upload DPR" : "View DPR";
  if (
    dprStatus === "DPR_DNO_QUERY" ||
    dprStatus === "DPR_SNO_QUERY" ||
    dprStatus === "DPR_ADMIN_QUERY"
  ) {
    label = "View Query";
  } else if (dprStatus === "DPR_REJECTED" && isEntity) {
    label = "Resubmit DPR";
  } else if (
    dprStatus === "DPR_SUBMIT" ||
    dprStatus === "DPR_UNDER_SNO" ||
    dprStatus === "DPR_UNDER_ADMIN"
  ) {
    label = "View DPR";
  }

  return (
    <Box
      component={RouterLink}
      to={`${path.dprForm}?${createSearchParams({
        landOwnershipEpid: params.row.id,
      })}`}
      sx={{ ...defaultStyles.renderButtonText3 }}
    >
      {label}
    </Box>
  );
};

export const RenderDprActions = (params) => {
  const user = useAppSelector((state) => state.root.user);
  const [queryOpen, setQueryOpen] = useState(false);
  const [queryText, setQueryText] = useState("");

  const { mutate: reviewMutate, isLoading } = useMutation(
    "reviewDpr",
    (payload: { epid: string; action: "APPROVE" | "QUERY"; query?: string }) =>
      reviewDpr(payload),
    {
      onSuccess: () => {
        setQueryOpen(false);
        setQueryText("");
        // Trigger table refetch by reloading
        window.location.reload();
      },
    }
  );

  const status = params.row.status;
  const epid = params.row.id;
  const role = user.data.role;
  const userType = user.data.userType;

  // DNO/IO: userType is INDIVIDUAL and role is NOT Approver/Approver-view-only, OR userType is null (IO)
  const isDNO =
    (userType === "INDIVIDUAL" &&
      role !== "Approver" &&
      role !== "Approver-view-only") ||
    !userType;

  // SNO: userType is INDIVIDUAL and role is Approver or Approver-view-only
  const isSNO =
    userType === "INDIVIDUAL" &&
    (role === "Approver" || role === "Approver-view-only");

  // DNO/IO can act on DPR_SUBMIT status
  const canDNOAct = isDNO && status === "DPR_SUBMIT";
  // SNO can act on DPR_UNDER_SNO status
  const canSNOAct = isSNO && status === "DPR_UNDER_SNO";

  if (!canDNOAct && !canSNOAct) {
    return <Box sx={{ color: "#999" }}>—</Box>;
  }

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <MuiButton
        variant="contained"
        size="small"
        color="success"
        disabled={isLoading}
        onClick={() => reviewMutate({ epid, action: "APPROVE" })}
        sx={{ textTransform: "none", fontSize: 12 }}
      >
        Approve
      </MuiButton>
      <MuiButton
        variant="outlined"
        size="small"
        color="warning"
        disabled={isLoading}
        onClick={() => setQueryOpen(true)}
        sx={{ textTransform: "none", fontSize: 12 }}
      >
        Query
      </MuiButton>

      <Dialog
        open={queryOpen}
        onClose={() => setQueryOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Raise Query</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Query / Remark"
            fullWidth
            multiline
            rows={3}
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setQueryOpen(false)}>Cancel</MuiButton>
          <MuiButton
            variant="contained"
            disabled={!queryText.trim() || isLoading}
            onClick={() =>
              reviewMutate({ epid, action: "QUERY", query: queryText.trim() })
            }
          >
            Submit Query
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const RenderMOUButton = (params) => {
  const [open, setOpen] = useState(false);
  const [mouFile, setMouFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [queryOpen, setQueryOpen] = useState(false);
  const [queryText, setQueryText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const user = useAppSelector((state) => state.root.user);
  const role = user.data.role;
  const userType = user.data.userType;

  const epid = params.row.id;
  const rowStatus = params.row.rawDprStatus;

  // Entity = ORGANIZATION userType
  const isEntity = userType === "ORGANIZATION";

  // IO/DNO: userType is null/empty (Implementing Officer)
  const isDNO = !userType;

  // SNO (Nodal Officer): userType === "INDIVIDUAL"
  const isSNO = userType === "INDIVIDUAL";

  // Fetch MOU data
  const {
    data: mouResponse,
    isLoading: isMouLoading,
    refetch: refetchMou,
  } = useQuery(["mouByEpid", epid, open], () => getMouByEpid(epid), {
    enabled: open && rowStatus === "DPR_APPROVED",
  });

  const mouData = (mouResponse as any)?.data?.mou || null;
  const mouQueries = (mouResponse as any)?.data?.queries || [];
  const mouStatus = mouData?.status || null;
  const mouId = mouData?.id || null;

  // Pre-fetch signed URL for the uploaded document
  const { data: fileUrlResponse } = useQuery(
    ["mouFileUrl", mouData?.documentKey],
    () => getFileByKey(mouData.documentKey),
    {
      enabled: !!mouData?.documentKey && open,
    }
  );
  const documentUrl =
    (fileUrlResponse as any)?.data?.data?.fileUrl ||
    (fileUrlResponse as any)?.data?.fileUrl ||
    null;

  // Review mutation (for DNO/SNO approve/query)
  const { mutate: reviewMutate, isLoading: isReviewing } = useMutation(
    "reviewMou",
    (payload: { mouId: string; action: "APPROVE" | "QUERY"; query?: string }) =>
      reviewMou(payload),
    {
      onSuccess: () => {
        setQueryOpen(false);
        setQueryText("");
        setSuccessMsg("Action completed successfully.");
        refetchMou();
      },
      onError: () => {
        setError("Review action failed. Please try again.");
      },
    }
  );

  // Submit mutation (entity submits MOU for review)
  const { mutate: submitMutate, isLoading: isSubmitting } = useMutation(
    "submitMou",
    (payload: { mouId: string }) => submitMou(payload),
    {
      onSuccess: () => {
        setSuccessMsg("MOU submitted for review.");
        refetchMou();
      },
      onError: () => {
        setError("Submit failed. Please try again.");
      },
    }
  );

  // Only show for DPR_APPROVED status
  if (rowStatus !== "DPR_APPROVED") {
    return <Box sx={{ color: "#999" }}>—</Box>;
  }

  // Can entity edit/upload? Only when no MOU exists or status is MOU_DNO_QUERY
  const canEntityUpload =
    isEntity && (!mouStatus || mouStatus === "MOU_DNO_QUERY");

  // Can entity submit?
  const canEntitySubmit =
    isEntity &&
    mouData &&
    (mouStatus === "MOU_DRAFT" || mouStatus === "MOU_DNO_QUERY");

  // Can DNO act?
  const canDNOAct =
    isDNO && (mouStatus === "MOU_SUBMIT" || mouStatus === "MOU_SNO_QUERY");
  // Can SNO act?
  const canSNOAct =
    isSNO && (mouStatus === "MOU_UNDER_SNO" || mouStatus === "MOU_ADMIN_QUERY");

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      setError("Only PDF, DOC, DOCX, JPEG, and PNG files are allowed.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB.");
      return;
    }

    setError(null);
    setMouFile(file);
  };

  const handleUpload = async () => {
    if (!mouFile) return;

    setIsUploading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      // Step 1: Upload file to storage
      const formDataUpload = new FormData();
      formDataUpload.append("file", mouFile);
      const res = await fileUpload(formDataUpload as any);
      const uploaded =
        (res as any)?.data?.data?.uploadedFile ||
        (res as any)?.data?.uploadedFile;

      if (!uploaded?.key) {
        setError("File upload failed. Please try again.");
        return;
      }

      // Step 2: Create or update MOU record
      if (mouData) {
        // Update existing MOU document
        await updateMouDocument({
          mouId: mouData.id,
          documentKey: uploaded.key,
          documentName: mouFile.name,
        });
      } else {
        // Create new MOU record
        await uploadMou({
          epid,
          documentKey: uploaded.key,
          documentName: mouFile.name,
        });
      }

      setSuccessMsg("MOU document uploaded successfully!");
      setMouFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      refetchMou();
    } catch (err) {
      setError("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setMouFile(null);
    setError(null);
    setSuccessMsg(null);
    setQueryOpen(false);
    setQueryText("");
  };

  const MOU_STATUS_LABELS: Record<string, string> = {
    MOU_DRAFT: "Draft",
    MOU_SUBMIT: "Submitted",
    MOU_DNO_QUERY: "DNO Query",
    MOU_UNDER_SNO: "Under SNO Review",
    MOU_SNO_QUERY: "SNO Query",
    MOU_UNDER_ADMIN: "Under Admin Review",
    MOU_ADMIN_QUERY: "Admin Query",
    MOU_APPROVED: "Approved",
  };

  const MOU_STATUS_COLORS: Record<string, string> = {
    MOU_DRAFT: "#757575",
    MOU_SUBMIT: "#1976d2",
    MOU_DNO_QUERY: "#ed6c02",
    MOU_UNDER_SNO: "#1976d2",
    MOU_SNO_QUERY: "#ed6c02",
    MOU_UNDER_ADMIN: "#1976d2",
    MOU_ADMIN_QUERY: "#ed6c02",
    MOU_APPROVED: "#2e7d32",
  };

  return (
    <>
      <MuiButton
        variant="outlined"
        size="small"
        onClick={() => setOpen(true)}
        sx={{ textTransform: "none", fontSize: 12 }}
        startIcon={<DescriptionIcon />}
      >
        View/Upload MOU
      </MuiButton>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          MOU Document
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isMouLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {successMsg && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {successMsg}
                </Alert>
              )}

              {/* Project Details Section */}
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: "#f8f9fa",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, fontWeight: 600, color: "#333" }}
                >
                  Project Details
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Registration No.
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {params.row.registrationNo || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Entity Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {params.row.entityName || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      State
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {params.row.state || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      District
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {params.row.district || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Division
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {params.row.division || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Total Area (Ha)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {params.row.totalArea || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* MOU Status Section */}
              {mouData && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "#333" }}
                    >
                      Current MOU Status
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: MOU_STATUS_COLORS[mouStatus] || "#333",
                      }}
                    >
                      {MOU_STATUS_LABELS[mouStatus] || mouStatus}
                    </Typography>
                  </Box>
                  {mouData.documentName && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <DescriptionIcon color="primary" sx={{ fontSize: 20 }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {mouData.documentName}
                      </Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        title="View Document"
                        disabled={!documentUrl}
                        onClick={() => {
                          if (documentUrl) window.open(documentUrl, "_blank");
                        }}
                      >
                        <VisibilityIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        title="Download Document"
                        disabled={!documentUrl}
                        onClick={() => {
                          if (documentUrl) {
                            const a = document.createElement("a");
                            a.href = documentUrl;
                            a.download = mouData.documentName || "mou-document";
                            a.target = "_blank";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }
                        }}
                      >
                        <DownloadIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Upload Section — Only for Entity in editable status */}
              {canEntityUpload && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1.5, fontWeight: 600, color: "#333" }}
                  >
                    {mouData ? "Update MOU Document" : "Upload MOU Document"}
                  </Typography>

                  <Box
                    sx={{
                      border: "2px dashed #ccc",
                      borderRadius: 2,
                      p: 3,
                      textAlign: "center",
                      cursor: "pointer",
                      mb: 2,
                      "&:hover": {
                        borderColor: "#1976d2",
                        bgcolor: "#f5f5f5",
                      },
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <CloudUploadIcon
                      sx={{ fontSize: 40, color: "#999", mb: 1 }}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {mouFile
                        ? mouFile.name
                        : "Click to select MOU document (PDF, DOC, DOCX, JPEG, PNG)"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Max file size: 10MB
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleFileSelect}
                    />
                  </Box>

                  <Box
                    sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                  >
                    <MuiButton
                      variant="contained"
                      disabled={!mouFile || isUploading}
                      onClick={handleUpload}
                      startIcon={
                        isUploading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <CloudUploadIcon />
                        )
                      }
                    >
                      {isUploading
                        ? "Uploading..."
                        : mouData
                        ? "Update Document"
                        : "Upload"}
                    </MuiButton>
                  </Box>
                </>
              )}

              {/* Submit Button — Entity can submit MOU for review */}
              {canEntitySubmit && (
                <Box sx={{ mt: 2 }}>
                  <MuiButton
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isSubmitting}
                    onClick={() => submitMutate({ mouId: mouData.id })}
                    startIcon={
                      isSubmitting ? <CircularProgress size={16} /> : null
                    }
                  >
                    {isSubmitting ? "Submitting..." : "Submit MOU for Review"}
                  </MuiButton>
                </Box>
              )}

              {/* DNO/SNO Review Actions */}
              {(canDNOAct || canSNOAct) && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1.5, fontWeight: 600, color: "#333" }}
                  >
                    Review Actions
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <MuiButton
                      variant="contained"
                      color="success"
                      disabled={isReviewing}
                      onClick={() => reviewMutate({ mouId, action: "APPROVE" })}
                      sx={{ textTransform: "none" }}
                    >
                      Approve
                    </MuiButton>
                    <MuiButton
                      variant="outlined"
                      color="warning"
                      disabled={isReviewing}
                      onClick={() => setQueryOpen(true)}
                      sx={{ textTransform: "none" }}
                    >
                      Raise Query
                    </MuiButton>
                  </Box>
                </Box>
              )}

              {/* Queries & Remarks History */}
              {mouQueries.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1.5, fontWeight: 600, color: "#333" }}
                  >
                    Queries & Remarks History
                  </Typography>
                  <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                    {mouQueries.map((q: any, index: number) => (
                      <Box key={q.id || index}>
                        {index > 0 && <Divider sx={{ my: 2 }} />}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "flex-start",
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor:
                                q.userType === "ADMIN"
                                  ? "#DC2626"
                                  : q.userType === "INDIVIDUAL"
                                  ? "#7C3AED"
                                  : q.userType === "ORGANIZATION"
                                  ? "#059669"
                                  : "#2563EB",
                              width: 36,
                              height: 36,
                              fontSize: "0.85rem",
                            }}
                          >
                            {(q.userName || "U").charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.9rem",
                                  color: "#1F2937",
                                }}
                              >
                                {q.userName || "Unknown User"}
                              </Typography>
                              <MuiChip
                                label={
                                  q.userType === "ADMIN"
                                    ? "Admin"
                                    : q.userType === "INDIVIDUAL"
                                    ? "Nodal Officer"
                                    : q.userType === "ORGANIZATION"
                                    ? "Entity"
                                    : "Implementing Officer"
                                }
                                size="small"
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 22,
                                  bgcolor:
                                    q.userType === "ADMIN"
                                      ? "#FEE2E2"
                                      : q.userType === "INDIVIDUAL"
                                      ? "#EDE9FE"
                                      : q.userType === "ORGANIZATION"
                                      ? "#D1FAE5"
                                      : "#DBEAFE",
                                  color:
                                    q.userType === "ADMIN"
                                      ? "#991B1B"
                                      : q.userType === "INDIVIDUAL"
                                      ? "#5B21B6"
                                      : q.userType === "ORGANIZATION"
                                      ? "#065F46"
                                      : "#1E40AF",
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: "#9CA3AF", ml: "auto" }}
                              >
                                {q.createdAt
                                  ? new Date(q.createdAt).toLocaleString(
                                      "en-IN",
                                      {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      }
                                    )
                                  : ""}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                color: "#4B5563",
                                fontSize: "0.875rem",
                                lineHeight: 1.6,
                                bgcolor: "#F9FAFB",
                                p: 1.5,
                                borderRadius: "8px",
                                border: "1px solid #E5E7EB",
                              }}
                            >
                              {q.query}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleClose}>Close</MuiButton>
        </DialogActions>
      </Dialog>

      {/* Query Dialog for DNO/SNO */}
      <Dialog
        open={queryOpen}
        onClose={() => setQueryOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Raise Query</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label=""
            fullWidth
            // multiline
            // rows={3}
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setQueryOpen(false)}>Cancel</MuiButton>
          <MuiButton
            variant="contained"
            disabled={!queryText.trim() || isReviewing}
            onClick={() =>
              reviewMutate({
                mouId,
                action: "QUERY",
                query: queryText.trim(),
              })
            }
          >
            Submit Query
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
